import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Sparkles } from "lucide-react";
import Header from "@/components/Header";
import { getPaymentStatus } from "@/lib/api";

const MAX_POLL_ATTEMPTS = 20;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | expired | error
  const [details, setDetails] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setErrorMsg("Missing session id.");
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const data = await getPaymentStatus(sessionId);
        if (cancelled) return;
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_POLL_ATTEMPTS) {
          setStatus("error");
          setErrorMsg("Payment status check timed out. If you completed payment, your credits will appear shortly.");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setErrorMsg(e?.response?.data?.detail || "Could not verify payment.");
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen grain" data-testid="payment-success-page">
      <Header />
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-16">
        <div className="border-2 border-[#111] bg-white shadow-brutal-lg p-8 md:p-12">
          {status === "checking" && (
            <div className="text-center" data-testid="payment-checking">
              <Loader2 size={48} className="mx-auto text-[#111] animate-spin" />
              <div className="mt-6 font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
                ▸ Verifying with Stripe
              </div>
              <h1 className="mt-2 font-serif-display text-3xl md:text-4xl font-black tracking-tight">
                Confirming your payment...
              </h1>
              <p className="mt-4 text-sm text-[#333]">Please don&apos;t close this tab. This usually takes a few seconds.</p>
            </div>
          )}

          {status === "paid" && (
            <div className="text-center" data-testid="payment-success">
              <CheckCircle2 size={56} className="mx-auto text-[#0A8754]" />
              <div className="mt-6 font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#0A8754]">
                ▸ Payment received
              </div>
              <h1 className="mt-2 font-serif-display text-4xl md:text-5xl font-black tracking-tight leading-none">
                You&apos;re in.
              </h1>
              <p className="mt-4 text-base text-[#333]">
                {details?.package_name && (<>Thanks for buying <strong>{details.package_name}</strong>. </>)}
                {details?.credits > 0 && (
                  <>
                    <strong>{details.credits} lookup{details.credits === 1 ? "" : "s"}</strong>
                    {details?.kind === "monthly_pass"
                      ? ` ${details.credits === 1 ? "was" : "were"} added — your plan is active and auto-renews monthly. Manage it anytime from your Account.`
                      : ` ${details.credits === 1 ? "was" : "were"} just added to your account.`}
                  </>
                )}
              </p>

              <div className="mt-6 border-2 border-[#111] bg-[#F4F4F0] p-5 flex items-center gap-4 text-left">
                <Sparkles className="text-[#FF331F] shrink-0" size={22} />
                <div>
                  <div className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555]">Next up</div>
                  <p className="mt-1 text-sm text-[#222]">
                    Head to <strong>My Blueprints</strong> to see your lookup balance, or jump back to any URL and hit rescan.
                  </p>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/my-reports"
                  className="inline-flex items-center justify-center gap-2 bg-[#111] text-[#F4F4F0] px-6 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#FF331F] transition-colors"
                  data-testid="go-to-reports"
                >
                  My Blueprints <ArrowRight size={14} />
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#111] px-6 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#F4F4F0]"
                  data-testid="go-to-home"
                >
                  Back to home
                </Link>
              </div>
            </div>
          )}

          {status === "expired" && (
            <div className="text-center" data-testid="payment-expired">
              <XCircle size={48} className="mx-auto text-[#F5A623]" />
              <h1 className="mt-4 font-serif-display text-3xl font-black">Session expired</h1>
              <p className="mt-3 text-sm text-[#333]">Your checkout session timed out. Head back to pricing to try again.</p>
              <Link
                to="/pricing"
                className="mt-6 inline-flex items-center gap-2 bg-[#111] text-[#F4F4F0] px-6 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#FF331F]"
              >
                Back to pricing
              </Link>
            </div>
          )}

          {status === "error" && (
            <div className="text-center" data-testid="payment-error">
              <XCircle size={48} className="mx-auto text-[#FF331F]" />
              <h1 className="mt-4 font-serif-display text-3xl font-black">Something went wrong</h1>
              <p className="mt-3 text-sm text-[#333]">{errorMsg}</p>
              <div className="mt-6 flex gap-3 justify-center">
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 bg-[#111] text-[#F4F4F0] px-6 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#FF331F]"
                >
                  Back to pricing
                </Link>
                <Link
                  to="/my-reports"
                  className="inline-flex items-center gap-2 border-2 border-[#111] px-6 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#F4F4F0]"
                >
                  My Blueprints
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
